<script setup>
import { computed } from 'vue';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import CsmlValidationStatus from './CsmlValidationStatus.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import InlineInput from 'dashboard/components-next/inline-input/InlineInput.vue';
import { TOPBAR_H } from '../../constants/csmlLayout';

defineProps({
  avatarUrl: { type: String, default: '' },
  title: { type: String, default: '' },
  isSaving: { type: Boolean, default: false },
  isPanMode: { type: Boolean, default: false },
  canUndo: { type: Boolean, default: false },
  canRedo: { type: Boolean, default: false },
  issues: { type: Array, default: () => [] },
  isDirty: { type: Boolean, default: false },
  showCode: { type: Boolean, default: false },
});

const emit = defineEmits([
  'back',
  'openSettings',
  'example',
  'tidy',
  'clear',
  'toggleCode',
  'undo',
  'help',
  'focusNode',
  'redo',
  'export',
  'copy',
  'save',
  'update:isPanMode',
]);

const botName = defineModel('botName', { type: String, default: '' });

const { t } = useI18n();

const [showMenu, toggleMenu] = useToggle();

const menuSections = computed(() => [
  {
    items: [
      {
        action: 'example',
        value: 'example',
        label: t('AGENT_BOTS.VISUAL_BUILDER.EXAMPLE'),
        icon: 'i-lucide-sparkles',
      },
      {
        action: 'tidy',
        value: 'tidy',
        label: t('AGENT_BOTS.VISUAL_BUILDER.TIDY_UP'),
        icon: 'i-lucide-layout-dashboard',
      },
      {
        action: 'export',
        value: 'export',
        label: t('AGENT_BOTS.VISUAL_BUILDER.EXPORT'),
        icon: 'i-lucide-download',
      },
      {
        action: 'copy',
        value: 'copy',
        label: t('AGENT_BOTS.VISUAL_BUILDER.COPY'),
        icon: 'i-lucide-copy',
      },
    ],
  },
  {
    items: [
      {
        action: 'delete',
        value: 'clear',
        label: t('AGENT_BOTS.VISUAL_BUILDER.CLEAR'),
        icon: 'i-lucide-trash-2',
      },
    ],
  },
]);

// DropdownMenu reports "delete" as the action for the destructive entry (it
// drives the ruby colour), so the real event name travels in `value`.
const onMenuAction = ({ value }) => {
  toggleMenu(false);
  if (value === 'example') emit('example');
  else if (value === 'tidy') emit('tidy');
  else if (value === 'export') emit('export');
  else if (value === 'copy') emit('copy');
  else if (value === 'clear') emit('clear');
};
</script>

<template>
  <header
    class="grid items-center gap-3 px-3 border-b grid-cols-[1fr_auto_1fr] border-n-weak bg-n-solid-1 shrink-0"
    :style="{ height: `${TOPBAR_H}px` }"
  >
    <div class="flex items-center min-w-0 gap-2">
      <Button
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-arrow-left"
        is-icon-only
        :title="t('AGENT_BOTS.VISUAL_BUILDER.BACK')"
        @click="emit('back')"
      />
      <button
        type="button"
        class="rounded-full shrink-0"
        :title="t('AGENT_BOTS.VISUAL_BUILDER.SETTINGS.TITLE')"
        @click="emit('openSettings')"
      >
        <Avatar
          :src="avatarUrl"
          :name="botName"
          :size="24"
          rounded-full
          icon-name="i-lucide-bot-message-square"
        />
      </button>
      <InlineInput
        v-model="botName"
        class="min-w-0 max-w-64"
        :title="title"
        :placeholder="t('AGENT_BOTS.VISUAL_BUILDER.BOT_NAME')"
        custom-input-class="font-semibold truncate"
      />
    </div>

    <div class="flex items-center gap-2">
      <div class="flex items-center gap-0.5 p-0.5 rounded-lg bg-n-alpha-2">
        <Button
          size="sm"
          :variant="isPanMode ? 'ghost' : 'solid'"
          :color="isPanMode ? 'slate' : 'blue'"
          icon="i-lucide-mouse-pointer-2"
          :label="t('AGENT_BOTS.VISUAL_BUILDER.SELECT')"
          @click="emit('update:isPanMode', false)"
        />
        <Button
          size="sm"
          :variant="isPanMode ? 'solid' : 'ghost'"
          :color="isPanMode ? 'blue' : 'slate'"
          icon="i-lucide-hand"
          :label="t('AGENT_BOTS.VISUAL_BUILDER.PAN')"
          @click="emit('update:isPanMode', true)"
        />
      </div>
      <div class="flex items-center gap-0.5">
        <Button
          size="sm"
          variant="ghost"
          color="slate"
          icon="i-lucide-undo-2"
          is-icon-only
          :disabled="!canUndo"
          :title="t('AGENT_BOTS.VISUAL_BUILDER.UNDO')"
          @click="emit('undo')"
        />
        <Button
          size="sm"
          variant="ghost"
          color="slate"
          icon="i-lucide-redo-2"
          is-icon-only
          :disabled="!canRedo"
          :title="t('AGENT_BOTS.VISUAL_BUILDER.REDO')"
          @click="emit('redo')"
        />
      </div>
    </div>

    <div class="flex items-center justify-end gap-1.5">
      <CsmlValidationStatus
        :issues="issues"
        :is-dirty="isDirty"
        @focus-node="emit('focusNode', $event)"
      />
      <Button
        size="sm"
        :variant="showCode ? 'solid' : 'faded'"
        color="slate"
        icon="i-lucide-code-xml"
        :label="t('AGENT_BOTS.VISUAL_BUILDER.CODE')"
        @click="emit('toggleCode')"
      />
      <Button
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-keyboard"
        is-icon-only
        :title="t('AGENT_BOTS.VISUAL_BUILDER.SHORTCUTS.TITLE')"
        @click="emit('help')"
      />
      <div v-on-clickaway="() => toggleMenu(false)" class="relative">
        <Button
          size="sm"
          variant="faded"
          color="slate"
          icon="i-lucide-ellipsis"
          is-icon-only
          :title="t('AGENT_BOTS.VISUAL_BUILDER.MORE_ACTIONS')"
          @click="toggleMenu()"
        />
        <DropdownMenu
          v-if="showMenu"
          :menu-sections="menuSections"
          class="mt-1 ltr:right-0 rtl:left-0 top-full w-48"
          @action="onMenuAction"
        />
      </div>
      <Button
        size="sm"
        color="blue"
        :label="t('AGENT_BOTS.VISUAL_BUILDER.SAVE')"
        :is-loading="isSaving"
        @click="emit('save')"
      />
    </div>
  </header>
</template>
